import React, { useState, useCallback } from 'react';
import { getTauriDesktopOs } from '../utils/platform';
import { doThisNowOnHost } from '../utils/desktopHostCopy';

const GOLD = '#C9A84C';

/**
 * Shown on the home library host before the user leaves home.
 * macOS: re-triggers the Local Network permission probe via Rust.
 * Windows/Linux: firewall prompts surface when the app binds to a port — acknowledge only.
 */
export default function HomeNetworkAccessModal({ open, onDismiss }) {
  const [busy, setBusy] = useState(false);

  const triggerPermission = useCallback(async () => {
    setBusy(true);
    try {
      const c = await import('../lib/connector');
      await c.warmLocalNetworkAccess();
    } catch (e) {
      console.warn('[HomeNetworkAccessModal] warm failed:', e);
    } finally {
      setBusy(false);
    }
  }, []);

  const acknowledge = useCallback(() => {
    try {
      localStorage.setItem('maestro_lan_setup_ack', Date.now().toString());
    } catch { /* ignore */ }
    onDismiss?.();
  }, [onDismiss]);

  if (!open) return null;

  const os = getTauriDesktopOs();
  const isMac = os === 'macos';

  const primaryBtnStyle = {
    padding: '12px 18px',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    background: GOLD,
    color: '#111',
    fontWeight: 700,
    fontSize: 14,
  };

  const secondaryBtnStyle = {
    padding: '11px 18px',
    borderRadius: 12,
    cursor: 'pointer',
    background: 'transparent',
    border: '1px solid var(--border-light)',
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontWeight: 600,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10050,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={acknowledge}
    >
      <div
        role="dialog"
        aria-labelledby="home-lan-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 440, width: '100%', borderRadius: 18, padding: '28px 26px',
          background: 'var(--bg-secondary, #1a1a1e)',
          border: '1px solid var(--border-light, rgba(255,255,255,0.08))',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div id="home-lan-title" style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
          Allow local network access
        </div>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.55 }}>
          {isMac ? (
            <>
              Before you leave home, macOS needs permission for Maestro to share your library with your
              other devices. When the system dialog appears, choose <strong>Allow</strong>.
            </>
          ) : os === 'windows' ? (
            <>
              Before you leave home, Windows needs to allow Maestro through your firewall so devices on
              your network can reach your library. When the Windows Defender Firewall prompt appears,
              choose <strong>Allow access</strong>. If prompted, set your current network to{' '}
              <strong>Private</strong> in Windows settings.
            </>
          ) : (
            <>
              Before you leave home, your firewall needs to allow Maestro through so it can share your
              library with your other devices. When the firewall prompt appears, choose{' '}
              <strong>Allow access</strong>.
            </>
          )}
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.5 }}>
          {doThisNowOnHost(os)} — you won&apos;t be able to approve it remotely later.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
          {isMac && (
            <button
              type="button"
              disabled={busy}
              onClick={triggerPermission}
              style={{
                ...primaryBtnStyle,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {busy ? 'Requesting…' : 'Show macOS Permission Dialog'}
            </button>
          )}
          <button
            type="button"
            onClick={acknowledge}
            style={isMac ? secondaryBtnStyle : primaryBtnStyle}
          >
            I&apos;ve allowed it — continue
          </button>
        </div>
      </div>
    </div>
  );
}
